import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { XCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '~/lib/hooks/useTranslation';

const WHATS_NEW_SEEN_KEY = 'whatsNewModalSeen_v3';

interface SlideConfig {
  titleKey: string;
  descriptionKey: string;
  learnMoreUrl: string;
  img?: string;
  video?: string;
  isFinal: boolean;
}

const SLIDES: SlideConfig[] = [
  {
    titleKey: 'whatsnewmodal.slide0.title',
    descriptionKey: 'whatsnewmodal.slide0.description',
    learnMoreUrl: '/pricing',
    img: '/images/whats-new-5x-offer-v3.webp',
    isFinal: false,
  },
  {
    titleKey: 'whatsnewmodal.slide1.title',
    descriptionKey: 'whatsnewmodal.slide1.description',
    learnMoreUrl: 'https://alphadocs.dualite.dev/tips-and-techniques/interaction-mode',
    video: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/dualite-prod/annotate.webm',
    isFinal: false,
  },
  {
    titleKey: 'whatsnewmodal.slide2.title',
    descriptionKey: 'whatsnewmodal.slide2.description',
    learnMoreUrl: 'https://alphadocs.dualite.dev/tips-and-techniques/interaction-mode',
    video: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/dualite-prod/inputbox_interact.webm',
    isFinal: false,
  },
  {
    titleKey: 'whatsnewmodal.slide3.title',
    descriptionKey: 'whatsnewmodal.slide3.description',
    learnMoreUrl: 'https://www.youtube.com/watch?v=-NP4Rl1gfJg',
    img: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/what%27sNewAlpha/assets.webp',
    isFinal: true,
  },
];

export const WHATS_NEW_PILL_LABEL_KEY = 'whatsnewmodal.slide0.title';

export function markWhatsNewSeen() {
  if (typeof window !== 'undefined') {
    localStorage.setItem(WHATS_NEW_SEEN_KEY, '1');
  }
}

export function hasSeenWhatsNew() {
  if (typeof window === 'undefined') {
    return true;
  }

  return localStorage.getItem(WHATS_NEW_SEEN_KEY) === '1';
}

interface WhatsNewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const slideVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0 }),
};

function WhatsNewModalContent({ isOpen, onClose }: WhatsNewModalProps) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      setDirection(1);
    }
  }, [isOpen]);

  const slide = SLIDES[currentIndex];

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleClose = () => {
    markWhatsNewSeen();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <div onClick={handleClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />

          {/* Modal shell — fixed size, clips the sliding content */}
          <div
            className="relative z-10 w-full max-w-[570px] h-[540px] rounded-[24px] p-6 flex flex-col gap-5 shadow-2xl overflow-hidden"
            style={{
              background: 'var(--dualite-background-tertiary)',
              border: '1px solid var(--dualite-border-primary)',
            }}
          >
            {/* Header — outside the slider so it stays stable */}
            <div className="relative flex justify-center items-center h-[30px]">
              <h2
                className="text-xl font-medium leading-[1.2] tracking-tight"
                style={{ color: 'var(--dualite-text-primary)' }}
              >
                {t('whatsnewmodal.title')}
              </h2>
              <button
                onClick={handleClose}
                className="absolute right-0 transition-colors duration-200"
                style={{ color: 'var(--dualite-text-modal-tertiary)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--dualite-text-primary)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--dualite-text-modal-tertiary)')}
                aria-label={t('whatsnewmodal.close')}
              >
                <XCircle size={24} strokeWidth={1.5} />
              </button>
            </div>

            {/* Sliding content area — fills remaining space between header and footer */}
            <div className="relative flex-1 min-h-0 overflow-hidden">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentIndex}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ type: 'tween', duration: 0.3, ease: 'easeInOut' }}
                  className="absolute inset-0 flex flex-col gap-5"
                >
                  {/* Media — grows to fill available space */}
                  <div
                    className="flex-1 min-h-0 rounded-2xl flex justify-center items-center overflow-hidden w-full"
                    style={{
                      background: 'var(--dualite-background-primary)',
                      border: '1px solid var(--dualite-border-primary)',
                    }}
                  >
                    {slide.video ? (
                      <video
                        src={slide.video}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="rounded-lg"
                        style={{ objectFit: 'contain', width: '100%', height: '100%' }}
                      />
                    ) : (
                      <img
                        src={slide.img}
                        alt={t(slide.titleKey)}
                        className="rounded-lg"
                        style={{ objectFit: 'contain', width: '100%', height: '100%' }}
                      />
                    )}
                  </div>

                  {/* Text — natural height, never pushes footer */}
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <h3 className="text-lg font-medium leading-[1.2]" style={{ color: 'var(--dualite-text-primary)' }}>
                      {t(slide.titleKey)}
                    </h3>
                    <p className="text-base leading-[1.4]" style={{ color: 'var(--dualite-text-modal-secondary)' }}>
                      {t(slide.descriptionKey)}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer — stable, outside the slider */}
            <div className="flex justify-between items-center w-full pt-1">
              {/* Pagination dots */}
              <div className="flex gap-1 items-center">
                {SLIDES.map((_, i) => (
                  <div
                    key={i}
                    className="transition-all duration-300 cursor-pointer"
                    style={{
                      width: i === currentIndex ? '21px' : '6px',
                      height: '6px',
                      borderRadius: i === currentIndex ? '3px' : '50%',
                      background:
                        i === currentIndex ? 'var(--dualite-text-primary)' : 'var(--dualite-text-modal-secondary)',
                    }}
                    onClick={() => setCurrentIndex(i)}
                  />
                ))}
              </div>

              {/* Buttons */}
              <div className="flex gap-4 items-center">
                <a
                  href={slide.learnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[14px] font-medium px-3 py-2 rounded-lg transition-colors duration-200"
                  style={{ color: 'var(--dualite-text-modal-secondary)' }}
                >
                  {t('whatsnewmodal.learnMore')}
                </a>
                <button
                  onClick={slide.isFinal ? handleClose : handleNext}
                  className="text-[14px] font-medium px-4 py-2 rounded-lg transition-all duration-200 active:scale-95 text-dualite-text-cta bg-dualite-background-cta"
                >
                  {slide.isFinal ? t('whatsnewmodal.close') : t('whatsnewmodal.next')}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function WhatsNewModal({ isOpen, onClose }: WhatsNewModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  return createPortal(<WhatsNewModalContent isOpen={isOpen} onClose={onClose} />, document.body);
}
